(() => {
  function getMainQty(form) {
    const qtyInput = form.querySelector('input[name="quantity"]');
    const qty = qtyInput ? parseInt(qtyInput.value || "1", 10) : 1;
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }

  function getMainVariantId(form) {
    const idInput = form.querySelector('input[name="id"]');
    return idInput?.value ? Number(idInput.value) : null;
  }

  function safeInt(val, fallback = 1) {
    const n = parseInt(val, 10);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  }

  async function addItems(items) {
    if (!items.length) return;

    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items }),
    });

    if (!res.ok) {
      let msg = "";
      try {
        const data = await res.json();
        msg = data?.description || data?.message || data?.error || JSON.stringify(data);
      } catch (_) {
        msg = await res.text();
      }
      throw new Error(msg || "Failed to add add-ons");
    }

    return res.json();
  }

  async function getCart() {
    const res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
    if (!res.ok) throw new Error("Failed to fetch cart");
    return res.json();
  }

  async function waitForMainInCart(mainVariantId, targetQty, timeoutMs = 3000) {
    const start = Date.now();

    while (Date.now() - start < timeoutMs) {
      const cart = await getCart();
      const line = cart.items?.find((it) => Number(it.id) === Number(mainVariantId));
      if (line && Number(line.quantity) >= Number(targetQty)) return cart;
      await new Promise((r) => setTimeout(r, 150));
    }
    return null;
  }

  async function refreshCartUI() {
    try {
      const res = await fetch("/?sections=cart-drawer,cart-icon-bubble", {
        headers: { Accept: "application/json" },
      });

      const sections = await res.json();
      if (!sections || typeof sections !== "object") return;

      const drawerEl = document.querySelector("cart-drawer");
      if (drawerEl && sections["cart-drawer"]) {
        const parsed = new DOMParser().parseFromString(sections["cart-drawer"], "text/html");
        const newDrawer = parsed.querySelector("cart-drawer");
        if (newDrawer) drawerEl.innerHTML = newDrawer.innerHTML;
      }

      const bubble = document.querySelector("#cart-icon-bubble");
      if (bubble && sections["cart-icon-bubble"]) {
        const parsed = new DOMParser().parseFromString(sections["cart-icon-bubble"], "text/html");
        const newBubble = parsed.querySelector("#cart-icon-bubble");
        if (newBubble) bubble.innerHTML = newBubble.innerHTML;
      }
    } catch (err) {
      console.warn("[Add-ons] UI refresh failed (non-blocking):", err);
    }
  }

  // Collect addon items from checkbox/radio mode
  function getAddonItemsCheckboxMode(container) {
    const items = [];

    // Required add-ons
    container.querySelectorAll('[data-required-addons] [data-addon-variant-id]').forEach((el) => {
      const id = el.getAttribute("data-addon-variant-id");
      if (!id) return;
      items.push({ id: Number(id), quantity: 1 });
    });

    // Optional add-ons
    container.querySelectorAll('[data-optional-addons] [data-addon-option]').forEach((opt) => {
      const input = opt.querySelector('input[type="checkbox"], input[type="radio"]');
      if (!input || !input.checked) return;

      const variantSelect = opt.querySelector("[data-addon-variant-select]");
      const variantId = variantSelect ? variantSelect.value : input.value;
      if (!variantId) return;

      const qtyInput = opt.querySelector("[data-addon-qty]");
      const qty = safeInt(qtyInput?.value || "1", 1);

      items.push({ id: Number(variantId), quantity: qty });
    });

    return items;
  }

  // Dropdown mode wiring: product dropdown populates variants dropdown, and shows qty
  function wireDropdownMode(container) {
    const productDD = container.querySelector("[data-addon-product-dropdown]");
    const variantDD = container.querySelector("[data-addon-variant-dropdown]");
    const qtyInput = container.querySelector("[data-addon-qty]");
    if (!productDD || !variantDD || !qtyInput) return;

    const variantDataByHandle = {};
    container.querySelectorAll("[data-addon-variants-json]").forEach((script) => {
      const handle = script.getAttribute("data-product-handle");
      if (!handle) return;
      try {
        variantDataByHandle[handle] = JSON.parse(script.textContent || "[]");
      } catch (_) {
        variantDataByHandle[handle] = [];
      }
    });

    const renderVariants = (handle) => {
      const variants = variantDataByHandle[handle] || [];

      variantDD.innerHTML = "";
      variants.forEach((v) => {
        const opt = document.createElement("option");
        opt.value = String(v.id);
        opt.textContent = v.title + (v.available ? "" : " (Sold out)");
        if (!v.available) opt.disabled = true;
        opt.setAttribute("data-sku", v.sku || "");
        opt.setAttribute("data-price", v.price || "");
        variantDD.appendChild(opt);
      });

      const hasVariants = variants.length > 0;
      variantDD.style.display = hasVariants ? "" : "none";
      qtyInput.style.display = hasVariants ? "" : "none";
    };

    productDD.addEventListener("change", () => {
      const handle = productDD.value;
      if (!handle) {
        variantDD.style.display = "none";
        qtyInput.style.display = "none";
        variantDD.innerHTML = "";
        return;
      }
      renderVariants(handle);
    });
  }

  function getAddonItemsDropdownMode(container) {
    const items = [];

    // Required add-ons
    container.querySelectorAll('[data-required-addons] [data-addon-variant-id]').forEach((el) => {
      const id = el.getAttribute("data-addon-variant-id");
      if (!id) return;
      items.push({ id: Number(id), quantity: 1 });
    });

    const productDD = container.querySelector("[data-addon-product-dropdown]");
    const variantDD = container.querySelector("[data-addon-variant-dropdown]");
    const qtyInput = container.querySelector("[data-addon-qty]");

    if (!productDD?.value) return items;
    if (!variantDD?.value) return items;

    const qty = safeInt(qtyInput?.value || "1", 1);
    items.push({ id: Number(variantDD.value), quantity: qty });
    return items;
  }

  // Update SKU/price display when variant select changes (checkbox/radio mode)
  function wireVariantUI(container) {
    if (!container) return;

    container.querySelectorAll("[data-addon-option]").forEach((opt) => {
      const sel = opt.querySelector("[data-addon-variant-select]");
      if (!sel) return;

      const skuEl = opt.querySelector("[data-addon-sku]");
      const priceEl = opt.querySelector("[data-addon-price]");
      const input = opt.querySelector('input[type="checkbox"], input[type="radio"]');

      const sync = () => {
        const option = sel.options[sel.selectedIndex];
        if (!option) return;

        if (input) input.value = sel.value;

        const sku = option.getAttribute("data-sku") || "";
        const price = option.getAttribute("data-price") || "";

        if (skuEl) {
          if (sku) {
            skuEl.style.display = "";
            skuEl.textContent = `SKU: ${sku}`;
          } else {
            skuEl.style.display = "none";
            skuEl.textContent = "";
          }
        }

        if (priceEl) {
          if (price && price.trim()) {
            priceEl.style.display = "";
            priceEl.textContent = `+${price}`;
          } else {
            priceEl.style.display = "none";
            priceEl.textContent = "";
          }
        }
      };

      sel.addEventListener("change", sync);
      sync();
    });
  }

  let inFlight = false;

  document.addEventListener(
    "submit",
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.action || !form.action.includes("/cart/add")) return;
      if (!form.closest('[id^="MainProduct-"]')) return;

      const addons = document.querySelector("[data-product-addons]");
      if (!addons) return;

      const mainVariantId = getMainVariantId(form);
      if (!mainVariantId) return;

      const mainQty = getMainQty(form);

      const style = addons?.dataset?.style || "checkbox";
      const addonItems =
        style === "dropdown" ? getAddonItemsDropdownMode(addons) : getAddonItemsCheckboxMode(addons);

      // If no add-ons selected (and no required), let the theme behave normally
      if (!addonItems.length) return;
      if (inFlight) return;

      const errorBox = addons.querySelector("[data-addon-error]");
      if (errorBox) {
        errorBox.style.display = "none";
        errorBox.textContent = "";
      }

      inFlight = true;

      // Let the theme add the main product normally
      (async () => {
        try {
          const cart = await waitForMainInCart(mainVariantId, mainQty, 3500);

          // Prevent “add-on only” carts if main isn't confirmed yet
          if (!cart) {
            console.warn("[Add-ons] Main item not detected yet; skipping add-ons to avoid add-on-only cart.");
            return;
          }

          await addItems(addonItems);
          await refreshCartUI();

          document.dispatchEvent(new CustomEvent("cart:refresh"));
          document.dispatchEvent(new CustomEvent("cart:update"));
        } catch (err) {
          console.error("[Add-ons] Failed:", err);
          if (errorBox) {
            errorBox.style.display = "block";
            errorBox.textContent = err?.message || "Could not add add-on to cart. Please try again.";
          }
        } finally {
          setTimeout(() => (inFlight = false), 450);
        }
      })();
    },
    true
  );

  function init() {
    const root = document.querySelector("[data-product-addons]");
    if (!root) return;

    wireVariantUI(root);

    const style = root?.dataset?.style || "checkbox";
    if (style === "dropdown") wireDropdownMode(root);
  }

  document.addEventListener("DOMContentLoaded", init);
  document.addEventListener("shopify:section:load", init);
})();
